// Keep first, in its own import group so import sorting leaves it there: telemetry must be initialized before
// application modules (store, auth) evaluate.
import './telemetryStartup';

import { initializeIcons } from '@fluentui/react';
import { createBrowserHistory } from 'history';
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import 'src/web.config';
import App from './App';
import { appFeatures, appSettings } from './common';
import { MsalProvider } from './components/auth/MsalProvider';
import { ContactUsAgent } from './components/copilotkit/ContactUsAgent';
import { useShowContactUsAgent } from './hooks/useShowContactUsAgent';
import './index.scss';
import { store } from './state/reduxStore';

initializeIcons();
appFeatures.initialize();
appSettings.initialize();

export const history = createBrowserHistory();

declare global {
  interface Window {
    AppFeatures: any;
  }
}

window.AppFeatures = window.AppFeatures || {};

window.AppFeatures.log = () => {
  console.log('AppFeatures', this);
};

const disableReactDevTools = (): void => {
  const noop = (): void => undefined;
  const DEV_TOOLS = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;

  if (typeof DEV_TOOLS === 'object') {
    for (const [key, value] of Object.entries(DEV_TOOLS)) {
      DEV_TOOLS[key] = typeof value === 'function' ? noop : null;
    }
  }
};

// disable the react dev tools is production.
if (process.env.NODE_ENV === 'production') {
  disableReactDevTools();
}

// Intercept clicks on the UHF skip link (<a class="uhf-skip-link" href="">).
// The UHF component renders this link with href="" which the browser resolves to
// the current page URL, causing a hard reload. We prevent that and move focus
// to <main id="content"> instead.
// A keydown listener is also needed because <a> elements do not fire a click
// event on Space — the browser default scrolls the page instead.
const focusMainContent = (e: Event) => {
  e.preventDefault();
  const main = document.getElementById('content');
  if (main) {
    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
    }
    main.focus();
  }
};

document.addEventListener('click', (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (target.closest('.uhf-skip-link')) {
    focusMainContent(e);
  }
});

document.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === ' ' || e.key === 'Spacebar') {
    const target = e.target as HTMLElement;
    if (target.closest('.uhf-skip-link')) {
      focusMainContent(e);
    }
  }
});

const RootApp: React.FC = () => {
  const { showAgent: showCopilot } = useShowContactUsAgent();

  // CopilotKit only needs to wrap the chat overlay chrome (sidebar + floating button), which lives
  // inside ContactUsAgent. The rest of the app is intentionally kept outside the provider so <App>
  // mounts once and never re-mounts when CopilotKit eligibility changes. The Contact Us hero bar
  // (rendered inside the routes) communicates with the overlay via the 'copilot-hero-send' window
  // event instead of the context.
  return (
    <MsalProvider>
      <div className="index">
        {showCopilot && <ContactUsAgent />}
        <App history={history} />
      </div>
    </MsalProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Provider store={store}>
    <RootApp />
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
// serviceWorker.unregister();

